import { z } from 'zod';

import {
    FETCH_YOURSELF,
    PROMOZAVR_PROJECT,
    PROMOZAVR_STAGE,
    type SelectPromoModelInput,
    type SelectPromoModelResponse,
    type SelectPromoModelServerInput,
} from '../types/promozavr';

const promozavrFormatSchema = z.string();

const promoSchema = z
    .object({
        name: z.string(),
        stage: z.string(),
    })
    .passthrough();

const selectorMetadataSchema = z
    .object({
        queueName: z.string().optional(),
    })
    .passthrough();

const promoWithFormatSchema = z
    .object({
        promo: promoSchema,
        format: promozavrFormatSchema,
        isPersistent: z.boolean().optional(),
        selectorMetadata: selectorMetadataSchema.optional(),
    })
    .passthrough();

const translationLeafSchema = z
    .object({
        form: z.string(),
    })
    .passthrough();

const translationKeySchema = z
    .object({
        translations: z.record(z.string(), translationLeafSchema),
    })
    .passthrough();

const translationKeysetSchema = z
    .object({
        keys: z.record(z.string(), translationKeySchema),
    })
    .passthrough();

const translationsSchema = z.object({
    keysets: z.record(z.string(), translationKeysetSchema),
});

const promoImageBaseSchema = z
    .object({
        name: z.string(),
        theme: z.string(),
    })
    .passthrough();

const imageUrlsSchema = z.record(z.string(), z.string());

const promoImageSchema = promoImageBaseSchema.extend({
    urls: imageUrlsSchema,
});

const promoCssBackgroundSchema = promoImageBaseSchema.extend({
    css: z.string(),
});

const promoIconSchema = promoImageBaseSchema.extend({
    url: z.string(),
});

const promoAnimationSchema = promoImageBaseSchema.extend({
    type: z.string(),
    url: z.string(),
    autoplay: z.boolean().optional(),
    loop: z.boolean().optional(),
});

const imageStoreSchema = z.object({
    pictures: z.array(promoImageSchema),
    backgrounds: z.array(promoImageSchema),
    backgroundsCss: z.array(promoCssBackgroundSchema),
    icons: z.array(promoIconSchema),
    animations: z.array(promoAnimationSchema),
});

const selectPromoSuppliersDataSchema = z
    .object({
        bunkerConfigSupplierData: z.literal(FETCH_YOURSELF),
        billingDataSupplierData: z.literal(FETCH_YOURSELF),
        billingSubscriptionsDataSupplierData: z.literal(FETCH_YOURSELF),
        blackboxUserInfoSupplierData: z.literal(FETCH_YOURSELF),
        datasyncPromoDataSupplierData: z.literal(FETCH_YOURSELF),
        datasyncUserFeaturesSupplierData: z.literal(FETCH_YOURSELF),
        saasKvSegmentsSupplierData: z.literal(FETCH_YOURSELF),
    })
    .strict();

const selectPromoModelInputBaseSchema = z
    .object({
        project: z.literal(PROMOZAVR_PROJECT),
        stage: z.literal(PROMOZAVR_STAGE),
        lang: z.string().optional(),
        tld: z.string().optional(),
        testIds: z.array(z.string()),
        debugParams: z.string().optional(),
        skipPromos: z.array(z.string()),
        selectAllPromos: z.literal(false),
        usePersistentQueues: z.literal(false),
        suppliersData: selectPromoSuppliersDataSchema,
    })
    .strict();

export const selectPromoModelInputSchema: z.ZodType<SelectPromoModelInput> = selectPromoModelInputBaseSchema;

export const selectPromoModelServerInputSchema: z.ZodType<SelectPromoModelServerInput> = selectPromoModelInputBaseSchema
    .extend({
        fastFail: z.boolean().optional(),
    })
    .strict();

export const transportSelectPromoModelInputSchema = selectPromoModelInputSchema;

export const selectPromoModelResponseSchema = z
    .object({
        isSelected: z.boolean(),
        selectedPromos: z.array(promoWithFormatSchema).optional(),
        translations: translationsSchema.optional(),
        images: imageStoreSchema.optional(),
    })
    .passthrough() as z.ZodType<SelectPromoModelResponse>;
